import { Link, useParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles, categories } from "@/mocks/gossip";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const cat = categories.find((c) => c.slug === slug);

  if (!cat) {
    return (
      <>
        <Header />
        <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center px-4 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
            <i className="ri-folder-forbid-fill text-3xl" aria-hidden="true"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            该频道不存在
          </h1>
          <Link
            to="/"
            className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            <i className="ri-home-4-line" aria-hidden="true"></i> 返回首页
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  const list = articles.filter((a) => a.category === cat.slug);
  const cover = list[0]?.image;
  const others = categories.filter((c) => c.slug !== cat.slug);
  const channelHot = [...list].sort((a, b) => b.heat - a.heat).slice(0, 5);
  const tags = Array.from(new Set(list.flatMap((a) => a.tags))).slice(0, 8);

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://wind-in-soul.cn/" },
      { "@type": "ListItem", position: 2, name: cat.name },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: `{"@context":"https://schema.org","@type":"CollectionPage","name":"${cat.name} - 海角吃瓜","description":"${cat.desc}","url":"https://wind-in-soul.cn/category/${cat.slug}"}`,
        }}
      />
      <Header />
      <main>
        {/* 频道横幅 */}
        <section className="relative w-full overflow-hidden">
          {cover ? (
            <>
              <img src={cover} alt={`${cat.name}频道封面`} className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/70 to-foreground-950/40"></div>
            </>
          ) : (
            <div className="absolute inset-0 bg-foreground-950"></div>
          )}
          <div className="relative mx-auto w-full max-w-[1200px] px-4 py-14 md:px-8 md:py-20 lg:px-12">
            <nav className="flex items-center gap-1.5 text-sm text-background-200/80" aria-label="面包屑">
              <Link to="/" className="hover:text-background-50">首页</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <span className="text-accent-300">{cat.name}</span>
            </nav>
            <h1 className="mt-4 font-heading text-4xl font-black text-background-50 md:text-5xl">
              {cat.name}
            </h1>
            <p className="mt-2 text-lg font-semibold text-accent-300">{cat.tagline}</p>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-200/90">{cat.desc}</p>
            <div className="mt-6 flex flex-wrap items-center gap-3 text-sm text-background-100/90">
              <span className="rounded-full bg-background-50/15 px-3 py-1 backdrop-blur">
                <i className="ri-file-list-3-line mr-1" aria-hidden="true"></i>本频道 {cat.count} 篇精选
              </span>
              <span className="rounded-full bg-background-50/15 px-3 py-1 backdrop-blur">
                <i className="ri-fire-line mr-1" aria-hidden="true"></i>今日热度最高 {channelHot[0]?.heat ?? 0}
              </span>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="mx-auto w-full max-w-[1200px] px-4 md:px-8 lg:px-12">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
              {/* 列表 */}
              <div className="lg:col-span-8">
                <div className="flex items-center justify-between">
                  <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                    <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                      <i className="ri-stack-line text-sm" aria-hidden="true"></i>
                    </span>
                    最新精选
                  </h2>
                  <span className="text-xs text-foreground-500">共 {list.length} 篇 · 按热度排序</span>
                </div>

                {list.length === 0 ? (
                  <p className="mt-8 rounded-2xl border border-dashed border-background-300 p-16 text-center text-sm text-foreground-500">
                    该频道内容整理中，先去其它频道看看吧。
                  </p>
                ) : (
                  <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                    {list.map((item) => (
                      <ArticleCard key={item.id} item={item} />
                    ))}
                  </div>
                )}
              </div>

              {/* 侧栏 */}
              <aside className="lg:col-span-4">
                {/* 频道切换 */}
                <div className="rounded-2xl border border-background-200/70 bg-background-100 p-5">
                  <h3 className="text-sm font-bold text-foreground-900">切换频道</h3>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {others.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/category/${c.slug}`}
                        className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-background-200 px-3.5 py-1.5 text-xs font-semibold whitespace-nowrap text-foreground-600 transition-colors hover:bg-accent-200 hover:text-accent-900"
                      >
                        <i className="ri-hashtag" aria-hidden="true"></i>{c.name}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* 频道内最热 */}
                <div className="mt-5 rounded-2xl border border-background-200/70 bg-background-100 p-5">
                  <h3 className="flex items-center gap-2 text-sm font-bold text-foreground-900">
                    <span className="flex h-6 w-6 items-center justify-center rounded-md bg-accent-500 text-foreground-950">
                      <i className="ri-fire-fill text-xs" aria-hidden="true"></i>
                    </span>
                    {cat.name}·最热
                  </h3>
                  <ol className="mt-4 space-y-1">
                    {channelHot.map((a, idx) => (
                      <li key={a.id}>
                        <Link
                          to={`/detail/${a.id}`}
                          className="group flex items-start gap-3 rounded-lg px-2 py-2 hover:bg-background-200/70"
                        >
                          <span className="mt-0.5 w-5 shrink-0 font-heading text-lg font-black text-secondary-400">
                            {idx + 1}
                          </span>
                          <span className="line-clamp-2 text-sm font-medium leading-snug text-foreground-800 group-hover:text-primary-700">
                            {a.title}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* 标签 */}
                <div className="mt-5 rounded-2xl border border-background-200/70 bg-background-100 p-5">
                  <h3 className="text-sm font-bold text-foreground-900">相关标签</h3>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {tags.map((t) => (
                      <span key={t} className="rounded-full bg-background-200 px-3 py-1 text-xs whitespace-nowrap text-foreground-600">
                        #{t}
                      </span>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}